/* eslint-disable max-lines */
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import {
  SOURCE_OPTIONS,
  STATUS_OPTIONS
} from "../../utils/customerFeedback/labels";
import RangePicker from "../ui/datepicker/range/RangePicker";
import Input from "../ui/input/Input";
import { SelectOption } from "../ui/input/Input.types";
import SelectInput from "../ui/input/SelectInput";

const FeedbackFilters: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const source = searchParams.get("source") ?? "";
  const status = searchParams.get("status") ?? "";
  const hasEmail = searchParams.get("hasEmail") ?? "";
  const hasMessage = searchParams.get("hasMessage") ?? "";
  const hasReasons = searchParams.get("hasReasons") ?? "";
  const route = searchParams.get("route") ?? "";
  const dateFrom = searchParams.get("dateFrom") ?? "";
  const dateTo = searchParams.get("dateTo") ?? "";

  const [openDropdown, setOpenDropdown] = useState<
    "source" | "status" | "hasEmail" | "hasMessage" | "hasReasons" | null
  >(null);

  const update = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        next.delete("pageIndex");
        return next;
      },
      { replace: false }
    );
    setOpenDropdown(null);
  };

  const debouncedRoute = useMemo(
    () =>
      debounce((value: string) => {
        update("route", value.trim());
      }, 700),
    []
  );

  const onSearchInput = (event: ChangeEvent<HTMLInputElement>) =>
    debouncedRoute(event.currentTarget.value);

  const startOfDayISO = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}T00:00:00.000`;
  };
  const endOfDayISO = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}T23:59:59.999`;
  };

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!dateFrom && !dateTo) return undefined;
    return {
      from: dateFrom ? new Date(dateFrom) : undefined,
      to: dateTo ? new Date(dateTo) : undefined
    };
  }, [dateFrom, dateTo]);

  const handleDateChange = (r: DateRange | undefined) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("pageIndex");
        if (r?.from) next.set("dateFrom", startOfDayISO(r.from));
        else next.delete("dateFrom");
        if (r?.to) next.set("dateTo", endOfDayISO(r.to));
        else next.delete("dateTo");
        return next;
      },
      { replace: false }
    );
  };

  const clearAll = () => {
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const hasAnyFilter =
    source !== "" ||
    status !== "" ||
    hasEmail !== "" ||
    hasMessage !== "" ||
    hasReasons !== "" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    route !== "";

  const sourceOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "All Sources" },
      ...SOURCE_OPTIONS.map((o) => ({ id: String(o.value), name: o.label }))
    ],
    []
  );
  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "All Statuses" },
      ...STATUS_OPTIONS.map((o) => ({ id: String(o.value), name: o.label }))
    ],
    []
  );
  const hasEmailOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "All" },
      { id: "true", name: "With Email" },
      { id: "false", name: "Anonymous" }
    ],
    []
  );
  const hasMessageOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "All" },
      { id: "true", name: "With Message" },
      { id: "false", name: "Without Message" }
    ],
    []
  );
  const hasReasonsOptions = useMemo<SelectOption[]>(
    () => [
      { id: "", name: "All" },
      { id: "true", name: "With Reasons" },
      { id: "false", name: "Without Reasons" }
    ],
    []
  );

  const toggleDropdown = (
    key: "source" | "status" | "hasEmail" | "hasMessage" | "hasReasons"
  ) => setOpenDropdown((prev) => (prev === key ? null : key));

  return (
    <div className="mb-4 flex flex-col gap-3">
      <Input
        className="group w-full md:w-72"
        defaultValue={route}
        placeholder="Search route..."
        onChange={onSearchInput}
        inputClassName="group h-11 w-full pr-1"
        beforeClassName="text-neutral group-hover:text-black"
        before={
          <MagnifyingGlassIcon className="w-4 h-4" strokeWidth={2} />
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
        <SelectInput
          className="w-full sm:w-44"
          label="Source"
          placeholder="All Sources"
          options={sourceOptions}
          selectedId={source}
          isOpen={openDropdown === "source"}
          onClick={() => toggleDropdown("source")}
          onOptionSelect={(opt) => update("source", String(opt?.id ?? ""))}
        />

        <SelectInput
          className="w-full sm:w-44"
          label="Status"
          placeholder="All Statuses"
          options={statusOptions}
          selectedId={status}
          isOpen={openDropdown === "status"}
          onClick={() => toggleDropdown("status")}
          onOptionSelect={(opt) => update("status", String(opt?.id ?? ""))}
        />

        <SelectInput
          className="w-full sm:w-36"
          label="Email"
          placeholder="All"
          options={hasEmailOptions}
          selectedId={hasEmail}
          isOpen={openDropdown === "hasEmail"}
          onClick={() => toggleDropdown("hasEmail")}
          onOptionSelect={(opt) => update("hasEmail", String(opt?.id ?? ""))}
        />

        <SelectInput
          className="w-full sm:w-36"
          label="Message"
          placeholder="All"
          options={hasMessageOptions}
          selectedId={hasMessage}
          isOpen={openDropdown === "hasMessage"}
          onClick={() => toggleDropdown("hasMessage")}
          onOptionSelect={(opt) => update("hasMessage", String(opt?.id ?? ""))}
        />

        <SelectInput
          className="w-full sm:w-36"
          label="Reasons"
          placeholder="All"
          options={hasReasonsOptions}
          selectedId={hasReasons}
          isOpen={openDropdown === "hasReasons"}
          onClick={() => toggleDropdown("hasReasons")}
          onOptionSelect={(opt) => update("hasReasons", String(opt?.id ?? ""))}
        />

        <div className="w-full sm:w-[260px]">
          <RangePicker
            label="Period"
            placeholder="Select dates"
            range={dateRange}
            showApply={false}
            paddingType="hug"
            iconSide="right"
            iconColor="secondary"
            onChange={handleDateChange}
          />
        </div>

        {hasAnyFilter && (
          <button
            type="button"
            onClick={clearAll}
            className="col-span-2 self-end pb-3 text-sm text-gray-600 underline transition-colors
             hover:text-primary sm:col-span-1"
          >
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default FeedbackFilters;
